import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, TriangleAlert, Info, CircleX } from 'lucide-angular';

import { InstanceConfigService } from '../../../core/services/instance-config.service';

/**
 * Aviso de pago pendiente, arriba de todo.
 *
 * <p>Va en el shell y no en una pantalla concreta porque el mensaje tiene que
 * verse haga lo que haga el usuario: quien entra a mirar un contrato no pasa
 * por la pantalla de facturación, y ahí se pierde el aviso.
 *
 * <p>No se puede cerrar. Un banner descartable se descarta el primer día y
 * después nadie se entera de que la cuenta va a restringirse; el aviso
 * desaparece solo cuando el pago entra.
 */
@Component({
  selector: 'sr-aviso-cobranza',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    @if (cobranza(); as c) {
      <div class="ac" [attr.data-nivel]="nivel()" role="status" aria-live="polite">
        <lucide-angular [img]="icono()" [size]="17" />
        <span class="ac__texto">{{ c.mensaje }}</span>
        <a class="ac__contacto" href="mailto:contacto@softrent.cl">Contactar</a>
      </div>
    }
  `,
  styles: [`
    .ac {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 9px 20px;
      font-size: var(--text-xs, 12.5px);
      line-height: 1.4;
      border-bottom: 1px solid var(--hairline);
    }

    /* Tres niveles y no uno solo: un aviso que se ve igual el primer día que el
       día treinta no comunica que la situación cambió. */
    .ac[data-nivel="suave"]  { background: var(--paper-tint); color: var(--ink); }
    .ac[data-nivel="medio"]  { background: var(--warning-soft, #FDF3E3); color: var(--warning-strong, #B45309); }
    .ac[data-nivel="fuerte"] { background: var(--danger-soft, #FDECEC); color: var(--danger, #B91C1C); }

    .ac__texto { flex: 1; min-width: 0; }

    .ac__contacto {
      flex: none;
      font-weight: 600;
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    @media (max-width: 560px) {
      .ac { align-items: flex-start; padding: 10px 16px; }
    }
  `],
})
export class SrAvisoCobranzaComponent {
  private instanceConfig = inject(InstanceConfigService);

  readonly icons = { TriangleAlert, Info, CircleX };

  cobranza = computed(() => this.instanceConfig.cobranza());

  nivel = computed(() => {
    switch (this.cobranza()?.estado) {
      case 'POR_VENCER': return 'suave';
      case 'VENCIDO': return 'medio';
      default: return 'fuerte';
    }
  });

  icono = computed(() => {
    switch (this.cobranza()?.estado) {
      case 'POR_VENCER': return this.icons.Info;
      case 'SUSPENDIDO': return this.icons.CircleX;
      default: return this.icons.TriangleAlert;
    }
  });
}
